import { shuffle, type Rng } from './random.js'
import type { TeamDraft } from './schedule.js'

type Person = { id: string; name: string }

/**
 * Sorteggio delle squadre con le coppie fisse dell'area riservata (solo squadre da due): le coppie fisse restano
 * insieme, gli altri iscritti vengono mescolati e raggruppati, poi si mescolano di nuovo l'ordine di tutte le squadre
 * e l'ordine dei nomi dentro ognuna, così chi guarda l'estrazione non distingue una coppia fissa da una sorteggiata.
 * Le coppie che non possono valere (squadre non da due, id sconosciuti, un iscritto in due coppie) sono ignorate.
 */
export function drawTeams(
  participants: readonly Person[],
  playersPerTeam: number,
  pins: readonly (readonly [string, string])[],
  rng: Rng = Math.random,
): TeamDraft[] {
  const byId = new Map(participants.map((p) => [p.id, p]))
  const pinned = new Set<string>()
  const groups: Person[][] = []
  for (const [a, b] of playersPerTeam === 2 ? pins : []) {
    const first = byId.get(a)
    const second = byId.get(b)
    if (!first || !second || a === b || pinned.has(a) || pinned.has(b)) continue
    pinned.add(a)
    pinned.add(b)
    groups.push([first, second])
  }
  const rest = shuffle(participants.filter((p) => !pinned.has(p.id)), rng)
  for (let i = 0; i < rest.length; i += playersPerTeam) groups.push(rest.slice(i, i + playersPerTeam))
  return shuffle(groups, rng)
    .map((group) => shuffle(group, rng))
    .map((group) => ({ name: group.map((p) => p.name).join('/'), playerIds: group.map((p) => p.id) }))
}

/**
 * Estrazione live, fatta di clip da `DRAW_CLIP_MS`: l'introduzione (Zambe rovista nell'urna, due volte), un turno per
 * squadra e una chiusura. Il turno è la sequenza `DRAW_SLOT_CLIPS`: pesca (dig → pallina alzata), torna nell'urna,
 * pesca di nuovo, convalida (pollice e occhiolino), torna nell'urna per la coppia dopo.
 */
export const DRAW_CLIP_MS = 3_000
export const DRAW_INTRO_CLIP = 'rummage'
export const DRAW_SLOT_CLIPS = ['pick', 'back', 'pick', 'approve', 'next'] as const
export type DrawClip = typeof DRAW_INTRO_CLIP | (typeof DRAW_SLOT_CLIPS)[number]
export const DRAW_INTRO_MS = 2 * DRAW_CLIP_MS
export const DRAW_PER_TEAM_MS = DRAW_SLOT_CLIPS.length * DRAW_CLIP_MS
export const DRAW_OUTRO_MS = 3_000

/** Dentro il turno: ms dall'inizio in cui la pallina è in alto (un nome esce) e in cui arriva il pollice (coppia convalidata). */
const SLOT_STEPS = [
  { from: 0, phase: 'pick', shown: 0 },
  { from: 2_100, phase: 'reveal', shown: 1 },
  { from: 3_000, phase: 'pick', shown: 1 },
  { from: 8_100, phase: 'reveal', shown: 2 },
  { from: 10_500, phase: 'approved', shown: 2 },
] as const

export type DrawPhase = 'intro' | (typeof SLOT_STEPS)[number]['phase'] | 'outro' | 'done'
/** `team`: indice nell'ordine di estrazione (-1 nell'introduzione); `shown`: quanti nomi della coppia sono già usciti. */
export type DrawFrame = { phase: DrawPhase; team: number; shown: number }

export const drawDurationMs = (teamCount: number): number => DRAW_INTRO_MS + teamCount * DRAW_PER_TEAM_MS + DRAW_OUTRO_MS

/** Squadre già svelate a `elapsedMs` dall'inizio: la i-esima esce all'inizio del suo turno, mai prima. */
export function revealedTeams(elapsedMs: number, teamCount: number): number {
  if (elapsedMs < DRAW_INTRO_MS) return 0
  return Math.min(teamCount, Math.floor((elapsedMs - DRAW_INTRO_MS) / DRAW_PER_TEAM_MS) + 1)
}

/** Cosa mostra l'estrazione a `elapsedMs` dall'inizio: tutti i telefoni, dallo stesso orologio, vedono lo stesso momento. */
export function drawFrame(elapsedMs: number, teamCount: number): DrawFrame {
  if (elapsedMs < DRAW_INTRO_MS) return { phase: 'intro', team: -1, shown: 0 }
  const inTeams = elapsedMs - DRAW_INTRO_MS
  const team = Math.floor(inTeams / DRAW_PER_TEAM_MS)
  if (team >= teamCount) return { phase: elapsedMs < drawDurationMs(teamCount) ? 'outro' : 'done', team: teamCount, shown: 0 }
  const inSlot = inTeams - team * DRAW_PER_TEAM_MS
  let step: (typeof SLOT_STEPS)[number] = SLOT_STEPS[0]
  for (const s of SLOT_STEPS) if (inSlot >= s.from) step = s
  return { phase: step.phase, team, shown: step.shown }
}
